import { add, formatISO, parseISO } from 'date-fns'
import localforage from 'localforage'
import _ from 'lodash'
import { observable, computed, action, makeObservable, toJS } from 'mobx'
import pluralize from 'pluralize'
import { object } from 'underscore'
import OrganisationCollection from './Collections/OrganisationCollection'
import StaffCollection from './Collections/StaffCollection'
import OrganisationSubscriptionCollection from './Collections/OrganisationSubscriptionCollection'
import { hasImpersonationPermission } from './Permissions/HasPermissions'
import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'
import { version as appVersion } from '../../package.json'
import { getVersion } from '../Queries/version'
import { makeRequest } from '../Queries/makeRequest'

class SessionStore {
    @observable profilePictureUrl
    @observable emailVerified
    // @observable expires = null
    @observable accountId = null // Global account ID across all orgs
    @observable userId = null // Org-specific staff ID
    @observable userPermissions = null
    @observable userEmail = null
    @observable userFirstName = null
    @observable userLastName = null
    @observable userCreatedAt = null
    @observable organisationId = null
    @observable organisationName = null
    @observable organisationCountry = null
    @observable state = 'unauthenticated' //authenticated, checkingAuth, loggingIn
    @observable accounting = {}
    @observable initialized = false
    @observable updateAvailable = false
    @observable migratedToV2 = false

    @action.bound
    setMigratedToV2(migrated) {
        this.migratedToV2 = migrated
    }

    constructor(props) {
        makeObservable(this)
        this.initialize()
    }
    @action.bound
    async initialize() {
        await this.checkAuthentication()
        this.initialized = true
    }
    @computed
    get authenticated() {
        return (
            this.state === 'authenticated' &&
            // this.expires && //TODO: handle with workos
            this.userId //&&
            // this.expires > Date.now()
        )
    }

    @action.bound
    async login({ email, password, token } = {}) {
        this.state = 'loggingIn'
        try {
            if (email && password) {
                // Use Better Auth signIn
                const { authClient } = await import('../lib/auth-client')
                const response = await authClient.signIn.email({
                    email,
                    password,
                })

                if (response.error) {
                    throw new Error(response.error.message || 'Login failed')
                }

                // Get the updated session after login
                await this.checkAuthentication()
                return response
            } else if (token) {
                // Handle token-based auth if needed (for compatibility)
                return await this.saveLoginResponse({ data: token })
            }
        } catch (error) {
            Sentry.captureException(error)
            console.error(error)
            this.state = 'unauthenticated'
            throw error
        }
    }
    @action.bound
    async saveLoginResponse(response) {
        //TODO for ryan: you can use WorkOs attributes to implement more logic later
        const { data } = response
        //WorkOs attributes:
        this.emailVerified = data.emailVerified
        this.profilePictureUrl = data.profilePictureUrl
        // DB attributes
        this.userId = response.data.userId
        this.userPermissions = response.data.userPermissions
        this.userEmail = response.data.userEmail
        this.userFirstName = response.data.userFirstName
        this.userLastName = response.data.userLastName
        this.userCreatedAt = response.data.userCreatedAt
        this.organisationId = response.data.organisationId
        this.organisationName = response.data.organisationName
        this.organisationCountry = response.data.organisationCountry
        // this.expires = add(new Date(), {
        //     days: 13,
        //     hours: 23,
        //     minutes: 50,
        // })
        await this.saveSession()
        this.createModels()
        return response
    }

    @action.bound
    async logout() {
        try {
            // Use Better Auth signOut
            const { authClient } = await import('../lib/auth-client')
            await authClient.signOut()
            await this.clearCache()
        } catch (error) {
            console.error('Logout failed:', error)
            await this.clearCache()
        }
    }
    @action.bound
    async saveSession(callback) {
        const session = {
            emailVerified: this.emailVerified,
            profilePictureUrl: this.profilePictureUrl,
            accountId: this.accountId, // Add accountId to saved session
            userId: this.userId,
            userPermissions: toJS(this.userPermissions),
            userEmail: this.userEmail,
            userFirstName: this.userFirstName,
            userLastName: this.userLastName,
            userCreatedAt: this.userCreatedAt,
            organisationId: this.organisationId,
            organisationName: this.organisationName,
            organisationCountry: this.organisationCountry,
            // expires: this.expires && formatISO(this.expires),
            accounting: {
                expires:
                    this.accounting.expires &&
                    formatISO(this.accounting.expires),
                system: this.accounting.system,
            },
        }
        await localforage.setItem('session', session).then(() => {
            this.state = 'authenticated'
            callback && callback(session)
            return session
        })
    }

    @action.bound
    async clearCache() {
        const {
            collections,
            aggregateCollections,
        } = require('./CollectionLookup')
        await this.deleteSession()
        this.state = 'unauthenticated'
        this.accountId = null
        this.userId = null
        this.userPermissions = null
        this.userEmail = null
        this.userFirstName = null
        this.userLastName = null
        this.userCreatedAt = null
        this.organisationId = null
        this.organisationName = null
        this.organisationCountry = null
        this.accounting = {}
        collections.forEach((collection) => {
            collection.clear()
        })
        aggregateCollections.forEach((aggregate) => {
            aggregate.clear()
        })
    }
    @action.bound
    async deleteSession() {
        await localforage.removeItem('session')
    }

    @action.bound
    async checkAuthentication(callback) {
        this.state = 'checkingAuth'

        try {
            // Use Better Auth to get session
            const { authClient } = await import('../lib/auth-client')
            const sessionResponse = await authClient.getSession()
            const session = sessionResponse?.data?.session
            const user = sessionResponse?.data?.user

            if (!user || !session) {
                this.state = 'unauthenticated'
            } else {
                this.state = 'authenticated'

                // Map Better Auth user data to SessionStore properties
                this.emailVerified = user.emailVerified || false
                this.profilePictureUrl = user.image
                // Multi-org support: accountId is global, userId is org-specific staff.id
                this.accountId = user.accountId || user.id // Global account ID
                this.userId = user.staffId || user.id // Org-specific staff ID
                this.userEmail = user.email
                this.userFirstName =
                    user.firstName || user.name?.split(' ')[0] || ''
                this.userLastName =
                    user.lastName ||
                    user.name?.split(' ').slice(1).join(' ') ||
                    ''
                this.userCreatedAt = user.createdAt
                    ? new Date(user.createdAt).getTime()
                    : null

                // Extended staff data from customSession plugin
                this.userPermissions = user.permissions || []
                this.organisationId = user.organisationId || null
                this.organisationName = user.organisationName || null
                this.organisationCountry = user.organisationCountry || 'au'

                // Keep accounting session handling
                const savedSession = await localforage.getItem('session')
                if (savedSession?.accounting) {
                    this.accounting.expires =
                        savedSession.accounting.expires &&
                        parseISO(savedSession.accounting.expires)
                    this.accounting.system = savedSession.accounting.system
                    this.resetAccountingTimeout()
                }

                this.createModels()
                // Save the updated session to localStorage for backup
                await this.saveSession()
            }
        } catch (error) {
            console.error('Better Auth session check failed:', error)
            this.state = 'unauthenticated'
        }

        callback && callback()
    }
    createModels() {
        const user = this.createUserModel()
        const org = this.createOrganisationModel()
        // if (process.env.REACT_APP_ENV === 'production') {
        Sentry.setUser({
            email: user.email,
        })
        Sentry.setTag('name', user.fullName)
        Sentry.setTag('organisation', this.organisationName)
        posthog.identify(
            user.email, // Replace 'distinct_id' with your user's unique identifier
            {
                email: user.email,
                name: user.fullName,
                organisation: this.organisationName,
            } // optional: set additional user properties
        )
        posthog.group('organisation', this.organisationId, {
            name: this.organisationName,
        })
        // }
        window.Intercom('boot', {
            app_id: process.env.REACT_APP_INTERCOM_APP_ID,
            user_id: user.id,
            email: user.email,
            name: user.fullName,
            company: {
                name: this.organisationName,
                id: this.organisationId,
            },
            created_at: this.userCreatedAt,
        })
    }
    createUserModel() {
        const user = StaffCollection.add(
            {
                id: this.userId,
                permissions: this.userPermissions,
                email: this.userEmail,
                firstName: this.userFirstName,
                lastName: this.userLastName,
            },
            { trackUpdates: false }
        )
        return user
    }
    createOrganisationModel() {
        const organisation = OrganisationCollection.add(
            {
                id: this.organisationId,
                name: this.organisationName,
                country: this.organisationCountry,
            },
            { trackUpdates: false }
        )
        return organisation
    }
    @action.bound
    async resetAccountingTimeout() {
        if (this.accounting.expires > new Date()) {
            setTimeout(() => {
                this.accounting.expires = null
            }, this.accounting.expires - new Date())
        }
    }
    @action.bound
    async saveAccountingExpires(expires) {
        this.accounting.expires = expires
        this.accounting.system = this.organisation?.accountingSystem
        await this.saveSession()
        this.resetAccountingTimeout()
    }
    @computed
    get accountingConnected() {
        return (
            this.accounting.system === this.organisation?.accountingSystem &&
            this.accounting.expires &&
            this.accounting.expires > new Date()
        )
    }
    @computed
    get user() {
        return StaffCollection.staffsById[this.userId]
    }
    @computed
    get organisation() {
        return [...OrganisationCollection.organisations][0]
    }
    @computed
    get subscription() {
        return [...OrganisationSubscriptionCollection.models][0]
    }
    @computed
    get settings() {
        return {
            ...settingDefaults,
            ...(this.organisation?.settings || {}),
        }
    }
    @action
    async checkIfUpdateIsAvailable() {
        try {
            const versionData = await getVersion()

            // Safely extract and normalize deployed version
            const normalizeVersion = (version) => {
                if (version == null || typeof version === 'object') return null
                return String(version).trim().replace(/^v/, '') // Remove leading 'v' and whitespace
            }

            // Extract version with fallback priority: clientVersion → version → raw value
            let rawDeployedVersion
            if (versionData && typeof versionData === 'object') {
                rawDeployedVersion =
                    versionData.clientVersion ||
                    versionData.version ||
                    versionData
            } else {
                rawDeployedVersion = versionData
            }

            const deployedClientVersion = normalizeVersion(rawDeployedVersion)
            const currentAppVersion = normalizeVersion(appVersion)

            if (
                deployedClientVersion &&
                currentAppVersion &&
                deployedClientVersion === currentAppVersion
            ) {
                this.updateAvailable = false
            } else if (deployedClientVersion && currentAppVersion) {
                // No delay needed - database has the latest deployed version
                this.updateAvailable = true
            } else {
                // Unable to determine version, assume no update available
                this.updateAvailable = false
                console.warn(
                    'Unable to extract valid version from server response:',
                    versionData
                )
            }
        } catch (error) {
            // On error, assume no update is available and report to monitoring
            this.updateAvailable = false

            // Report to Sentry with contextual details
            Sentry.captureException(error, {
                tags: {
                    component: 'SessionStore',
                    operation: 'checkIfUpdateIsAvailable',
                },
                extra: {
                    currentAppVersion: appVersion,
                    stack: error.stack,
                },
            })

            // Report to PostHog for analytics
            posthog.capture('version_check_failed', {
                error: error.message,
                currentAppVersion: appVersion,
            })

            console.error('Failed to check for updates:', error)
        }
    }
}

export default new SessionStore()

export const settingDefaults = {
    sortPhasesBy: 'startDate',
    allowNoPhase: true,
    useTasks: true,
    allowAfterPhaseEnd: true,
    timeEntryAllocations: ['noAllocations', 'budgets', 'allocations'],
    timeEntryStatus: ['active', 'prospective', 'onHold', 'archived'],
    timeEntryFlags: ['billable', 'variation', 'overtime'],
    autoPopulate: [], // ["budgets", "allocations"]
    updateHoursFromRevenue: false,
    updateRevenueFromHours: false,
    autoUpdateRevenue: {
        action: 'ask', // "automatic", "never"
        adjustOnLoad: false,
        budget: 'remaining',
        start: 'now',
        end: 'endDate',
    },
    autoUpdateHours: {
        action: 'ask', // "automatic", "never"
        adjustOnLoad: false,
        budget: 'remaining',
        start: 'now',
        end: 'endDate',
    },
    reportInvoiceDateType: 'issueDate',
    savingInvoices: ['markTimeInvoiced'], // "lockTime", "automatic"
}
